import escapeHtml from "escape-html";
import { jsx } from "slate-hyperscript";
import { Text, Transforms } from "slate";

const serialize = (node) => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    if (node.bold) {
      string = `<strong>${string}</strong>`;
    }
    if (node.italic) {
      string = `<em>${string}</em>`;
    }
    if (node.underline) {
      string = `<u>${string}</u>`;
    }
    return string;
  }

  const children = node.children.map((n) => serialize(n)).join("");

  let style = "";

  if (node.align) {
    style = `"text-align: ${node.align};"`;
  }

  const elStyle = style.length > 0 ? ` style= ${style}` : ``;

  switch (node.type) {
    case "quote":
      return `<blockquote><p${elStyle}>${children}</p></blockquote>`;
    case "paragraph":
      return `<p${elStyle}>${children}</p>`;
    case "bulleted-list":
      return `<ul${elStyle}>${children}</ul>`;
    case "numbered-list":
      return `<ol${elStyle}>${children}</ol>`;
    case "list-item":
      return `<li${elStyle}>${children}</li>`;
    case "link":
      return `<a href="${escapeHtml(node.url)}"${elStyle}>${children}</a>`;
    case "image":
      return `<img src="${escapeHtml(node.url)}" alt="image"${elStyle}/>`;
    case "heading-one":
      return `<h1${elStyle}>${children}</h1>`;
    case "heading-two":
      return `<h2${elStyle}>${children}</h2>`;
    case "heading-three":
      return `<h3${elStyle}>${children}</h3>`;
    case "heading-four":
      return `<h4${elStyle}>${children}</h4>`;
    case "heading-five":
      return `<h5${elStyle}>${children}</h5>`;
    case "heading-six":
      return `<h6${elStyle}>${children}</h6>`;
    default:
      return children;
  }
};

const deserialize = (el, markAttributes = {}) => {
  if (el.nodeType === Node.TEXT_NODE) {
    return jsx("text", markAttributes, el.textContent);
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const nodeAttributes = { ...markAttributes };

  // define attributes for text nodes
  // eslint-disable-next-line default-case
  switch (el.nodeName) {
    case "STRONG":
      nodeAttributes.bold = true;
      nodeAttributes.align = "right";
      break;
    case "EM":
      nodeAttributes.italic = true;
      break;
    case "U":
      nodeAttributes.underline = true;
      break;
  }

  const children = Array.from(el.childNodes)
    .map((node) => deserialize(node, nodeAttributes))
    .flat();

  if (children.length === 0) {
    children.push(jsx("text", nodeAttributes, ""));
  }

  let style = {
    align: el.style?.textAlign,
  };

  switch (el.nodeName) {
    case "BODY":
      return jsx("fragment", {}, children);
    case "BR":
      return "\n";
    case "BLOCKQUOTE":
      return jsx("element", { type: "quote", ...style }, children);
    case "P":
      return jsx("element", { type: "paragraph", ...style }, children);
    case "UL":
      return jsx("element", { type: "bulleted-list", ...style }, children);
    case "OL":
      return jsx("element", { type: "numbered-list", ...style }, children);
    case "LI":
      return jsx("element", { type: "list-item", ...style }, children);
    case "H1":
      return jsx("element", { type: "heading-one", ...style }, children);
    case "H2":
      return jsx("element", { type: "heading-two", ...style }, children);
    case "H3":
      return jsx("element", { type: "heading-three", ...style }, children);
    case "H4":
      return jsx("element", { type: "heading-four", ...style }, children);
    case "H5":
      return jsx("element", { type: "heading-five", ...style }, children);
    case "H6":
      return jsx("element", { type: "heading-six", ...style }, children);
    case "IMG":
      return jsx(
        "element",
        { type: "image", url: el.getAttribute("src") },
        children,
      );
    case "A":
      return jsx(
        "element",
        { type: "link", url: el.getAttribute("href") },
        children,
      );
    default:
      return children;
  }
};

const cleanUpListItems = (parsedDocument) => {
  const listItems = parsedDocument.querySelectorAll("li");

  listItems.forEach((li) => {
    const pTag = li.querySelector("p");

    if (pTag) {
      const span = document.createElement("span");

      while (pTag.firstChild) {
        span.appendChild(pTag.firstChild);
      }

      li.replaceChild(span, pTag);
    }
  });
};

const withHtml = (editor) => {
  const { insertData, isInline } = editor;

  editor.isInline = (element) => {
    return element.type === "link" ? true : isInline(element);
  };

  editor.insertData = (data) => {
    const html = data.getData("text/html");

    if (html) {
      const parsed = new DOMParser().parseFromString(html, "text/html");

      cleanUpListItems(parsed);
      const fragment = deserialize(parsed.body);

      Transforms.insertFragment(editor, fragment);
      return;
    }

    insertData(data);
  };

  return editor;
};

export { withHtml, serialize, deserialize, cleanUpListItems };
